/**
 * Typed flow editor (Voluum, Tres Commas): raw GET/PUT; server snapshots on apply.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AlertCircle } from 'lucide-react';
import { trackerLabel } from '@trafficpulse/shared';

import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';

import { useStandardDraftContext } from '../../../standard-draft';
import { FlowMetaSection, StandardPathsWorkspace } from './sections';
import { useStandardFooterSlot } from './hooks/use-standard-footer-slot';
import StandardValidationBanner from './StandardValidationBanner';

export interface StandardConfigTabV2Props {
  flowId: string;
  killSwitch: boolean;
  standardTabActive: boolean;
}

const StandardConfigTabV2: FC<StandardConfigTabV2Props> = ({
  flowId,
  killSwitch,
  standardTabActive,
}) => {
  const { flowQuery, draftState, provider } = useStandardDraftContext();
  useStandardFooterSlot({ flowId, killSwitch, standardTabActive });

  if (flowQuery.isLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }
  if (flowQuery.isError || !draftState.draft) {
    return (
      <div className="flex min-h-0 flex-1 items-center">
        <div className="flex w-full items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            Failed to load flow from {trackerLabel(provider)}.{' '}
            {flowQuery.error instanceof Error ? flowQuery.error.message : ''}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      <div className="shrink-0 space-y-2 overflow-y-auto pr-1">
        {killSwitch && (
          <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
            Pushes are paused. Apply is disabled until you resume.
          </p>
        )}
        <StandardValidationBanner issues={draftState.issues} />
        <FlowMetaSection
          flowId={flowId}
          draft={draftState.draft}
          onPatch={draftState.patch}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden pr-1">
        <StandardPathsWorkspace
          draft={draftState.draft}
          onPatch={draftState.patch}
        />
      </div>
    </div>
  );
};

export default StandardConfigTabV2;
